import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react'
import { useAuth } from '../context/AuthContext'
import { useAccessContext } from '../context/AccessContext'
import {
  createDensityPreferenceStore,
  densityPreferenceKey,
  type DensityPreferenceScope,
  type DensitySnapshot,
  type InterfaceDensity,
} from '../services/interfacePreferences'

export interface DensityPreference extends DensitySnapshot {
  enabled: boolean
  setDensity: (density: InterfaceDensity) => void
}

/** Reusable scope-local hook; consumers pass a verified identity/context or null. */
export function useDensityPreference(scope: DensityPreferenceScope | null): DensityPreference {
  const key = densityPreferenceKey(scope)
  const store = useMemo(() => createDensityPreferenceStore(key), [key])
  const currentStore = useRef(store)
  currentStore.current = store
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
  const setDensity = useCallback((density: InterfaceDensity) => {
    // A deferred callback from an old scope must not write after identity changes.
    if (currentStore.current === store) store.setDensity(density)
  }, [store])
  return { ...snapshot, enabled: key !== null, setDensity }
}

/** Workforce adapter. It does not alter or substitute for backend authorization. */
export function useWorkspaceDensity(workspaceId: string): DensityPreference {
  const { user, loading: authLoading } = useAuth()
  const { accessContext, selectedTenantId, loading, error } = useAccessContext()
  const tenantId = accessContext?.tenantId
  const ready = !authLoading && !loading && !error && Boolean(
    user?.id && accessContext?.currentUser?.id === user.id && tenantId
    && (!selectedTenantId || selectedTenantId === tenantId),
  )
  return useDensityPreference(ready && user && tenantId ? { userId: user.id, tenantId, workspaceId } : null)
}
